import { APIServiceType } from '@/api/types';
import { Logger } from '../logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '../logger/datadog/types';
import {
  APIError,
  DefaultError,
  ErrorHandlerOptions,
  ErrorPayload,
  ErrorType,
  GeolocationHandlerError,
  HandleErrorParams,
  ParseError,
  SessionError,
} from './types';
import { setError } from '@/store/actions.ts';

/**
 * Handles errors by storing them in the error store and logging them to Datadog
 * @param error The error to handle
 * @param options Additional options for error handling
 * @param skipLogging Whether to skip logging the error to Datadog
 */
export const handleError = <
  TypeError extends {
    type: ErrorType;
    payload: {
      message?: string;
      name?: string;
      reason?: string;
      service?: APIServiceType;
      [string: string]: unknown;
    };
  },
>(
  error: TypeError,
  options: ErrorHandlerOptions & {
    [string: string]: unknown;
  } = {},
  skipLogging = false,
) => {
  const useCase = options.useCase || DdEventCategory.Initialization;
  const eventType = options.eventType || DdEventType.Error;
  const service = options.service || 'client';
  const stack = options.stack || new Error().stack;

  switch (error.type) {
    case ErrorType.SESSION: {
      setError(ErrorType.SESSION, (error as SessionError).payload);
      break;
    }
    case ErrorType.CLIENT:
      setError(ErrorType.CLIENT, (error as SessionError).payload);
      break;
    case ErrorType.API:
      setError(ErrorType.API, (error as APIError).payload);
      break;
    case ErrorType.PARSE:
      setError(ErrorType.PARSE, (error as ParseError).payload);
      break;

    case ErrorType.GEOLOCATION:
      setError(
        ErrorType.GEOLOCATION,
        (error as GeolocationHandlerError).payload,
      );
      break;
    case ErrorType.GAME:
      setError(ErrorType.GAME, error?.payload as ErrorPayload);
      break;
    case ErrorType.DEFAULT:
      setError(ErrorType.DEFAULT, (error as DefaultError).payload);
      break;
    default:
      setError(ErrorType.DEFAULT, {
        message: 'Unknown error type',
        reason: 'Unhandled error type',
      });
  }
  if (error.type === ErrorType.GAME) return;
  if (skipLogging || options.skipLogging) return;
  Logger.logRumError({
    name: error.payload.name ?? `[${error.type}] Error`,
    description: error.payload.message,
    useCase,
    eventType,
    service,
    stack,
    context: {
      ...options.context,
      error: error.payload,
      details: 'details' in error ? error.details : undefined,
    },
    payload: {
      reason: error.payload.reason,
      service: 'service' in error.payload ? error.payload.service : service,
    },
  });
};

/**
 * Wraps a function call in a try-catch block and handles any errors
 * @param callback The function to call
 * @param options Options for error handling
 * @returns The result of the callback function or undefined if an error occurred
 */
export const tryCatchWrapper = async <T>(
  callback: () => Promise<T> | T,
  options: ErrorHandlerOptions & {
    errorType: ErrorType;
    errorMessage: string;
    errorReason: string;
  },
): Promise<T | undefined> => {
  try {
    return await callback();
  } catch (error) {
    const { errorType, errorMessage, errorReason, ...handlerOptions } = options;

    let errorPayload: HandleErrorParams;
    switch (errorType) {
      case ErrorType.API:
        errorPayload = {
          type: ErrorType.API,
          payload: {
            message: errorMessage,
            reason: errorReason,
            service:
              (handlerOptions.service as unknown as APIServiceType) ||
              'unknown',
          },
        };
        break;
      case ErrorType.SESSION:
        errorPayload = {
          type: ErrorType.SESSION,
          payload: {
            message: errorMessage,
            reason: errorReason,
          },
          details: error,
        };
        break;
      case ErrorType.PARSE:
        errorPayload = {
          type: ErrorType.PARSE,
          payload: {
            message: errorMessage,
            reason: errorReason,
          },
          details: error,
        };
        break;
      case ErrorType.GEOLOCATION:
        errorPayload = {
          type: ErrorType.GEOLOCATION,
          payload: {
            message: errorMessage,
            reason: errorReason,
          },
          details: error,
        };
        break;
      case ErrorType.DEFAULT:
        errorPayload = {
          type: ErrorType.DEFAULT,
          payload: {
            message: errorMessage,
            reason: errorReason,
          },
          details: error,
        };
        break;
      case ErrorType.CLIENT:
      default:
        errorPayload = {
          type: ErrorType.CLIENT,
          payload: {
            message: errorMessage,
            reason: errorReason,
          },
        };
    }

    handleError(errorPayload as HandleErrorParams, handlerOptions);

    return undefined;
  }
};
